class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  isMobileConfirmationMode() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble, [data-cart-drawer-trigger]');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    document.addEventListener(
      'click',
      (event) => {
        const activeCartLink = event.target.closest(
          '#cart-icon-bubble, [data-cart-drawer-trigger], header a[href="/cart"], header a[href*="/cart"], .section-header a[href="/cart"], .section-header a[href*="/cart"]'
        );
        if (!activeCartLink) return;
        if (activeCartLink.closest('.seed-cart-drawer__ctas')) return;
        if (activeCartLink.closest('cart-drawer')) return;

        event.preventDefault();
        event.stopPropagation();
        this.open(activeCartLink);
      },
      true
    );
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.isMobileConfirmationMode()) {
      this.renderMobileConfirmation(parsedState);
      return;
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  renderMobileConfirmation(parsedState) {
    const updatedSection = this.getSectionDOM(parsedState.sections['cart-drawer']);
    const updatedConfirmation = updatedSection?.querySelector('seed-mobile-cart-confirmation');
    const confirmation = document.querySelector('seed-mobile-cart-confirmation');

    if (updatedConfirmation && confirmation) {
      confirmation.replaceWith(updatedConfirmation);
    }

    setTimeout(() => {
      const mobileConfirmation = document.querySelector('seed-mobile-cart-confirmation');
      if (mobileConfirmation?.open) {
        mobileConfirmation.open(parsedState);
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class SeedMobileCartConfirmation extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('.seed-mobile-cart-confirmation__dialog');
    this.closeButtons = this.querySelectorAll('[data-seed-cart-confirm-close]');
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close.bind(this)));
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  open(parsedState) {
    if (parsedState) this.updateProduct(parsedState);
    this.syncCheckoutTotal();
    this.hidden = false;
    window.requestAnimationFrame(() => {
      this.classList.add('is-active');
      document.body.classList.add('overflow-hidden');
      trapFocus(this, this.dialog || this);
    });
  }

  close() {
    this.classList.remove('is-active');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus();
    window.setTimeout(() => {
      if (!this.classList.contains('is-active')) this.hidden = true;
    }, 180);
  }

  updateProduct(parsedState) {
    const container = this.querySelector('[data-seed-cart-confirm-product]');
    if (!container) return;

    const image = parsedState.featured_image?.url
      ? `<img src="${this.escapeAttribute(parsedState.featured_image.url)}" alt="${this.escapeAttribute(parsedState.featured_image.alt || parsedState.product_title || parsedState.title || '')}" width="90" height="90" loading="lazy">`
      : '';
    const title = parsedState.product_title || parsedState.title || '';
    const price = this.formatLinePrice(parsedState);

    container.innerHTML = `
      <div class="seed-mobile-cart-confirmation__media">${image}</div>
      <div class="seed-mobile-cart-confirmation__product-info">
        <p class="seed-mobile-cart-confirmation__product-title">${this.escapeHtml(title)}</p>
        <p class="seed-mobile-cart-confirmation__product-price">${this.escapeHtml(price)}</p>
      </div>
    `;
  }

  syncCheckoutTotal() {
    const drawerTotal = document.querySelector('[data-seed-cart-drawer-checkout-total]')?.textContent?.trim();
    const checkoutTotal = this.querySelector('[data-seed-mobile-cart-confirm-total]');
    if (drawerTotal && checkoutTotal) checkoutTotal.textContent = drawerTotal;
  }

  formatLinePrice(parsedState) {
    const price = Number(parsedState.final_line_price || parsedState.line_price || parsedState.final_price || parsedState.price || 0);
    const quantity = Number(parsedState.quantity || 1);
    const linePrice = parsedState.final_line_price || parsedState.line_price ? price : price * quantity;
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(linePrice / 100);
    } catch (error) {
      return `$${(linePrice / 100).toFixed(2)}`;
    }
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  escapeAttribute(value = '') {
    return this.escapeHtml(value);
  }
}

customElements.define('seed-mobile-cart-confirmation', SeedMobileCartConfirmation);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
